/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { css } from "@emotion/react";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";

import type { ThemeType } from "../../../../global/emotion";
import { radius, spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useThemeValues } from "../../../hooks/useThemeValues";
import type { IconButtonProps } from "../_core/IconButton/IconButton.emotion";
import { IconButton } from "../_core/IconButton/IconButton.emotion";
import { Input } from "../_core/Input/Input.emotion";
import { Skeleton } from "../_core/Skeleton/Skeleton.emotion";
import { PanelLeftIcon } from "../icons/Icon/GeneralIcons";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./sheet.emotion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip.emotion";

// Constants
const SIDEBAR_COOKIE_NAME = "tool_sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "256px";
const SIDEBAR_WIDTH_MOBILE = "288px";
const SIDEBAR_WIDTH_ICON = "56px";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

// Context
type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

// Styles defined outside components
const sidebarWrapperStyles: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
  "&[data-variant='inset']": {
    backgroundColor: theme.semanticColors.neutralsLowest,
  },
});

const sidebarStyles: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  display: "flex",
  height: "100%",
  width: "var(--sidebar-width)",
  flexDirection: "column",
});

const sidebarGapStyles: Interpolation = {
  position: "relative",
  width: "var(--sidebar-width)",
  backgroundColor: "transparent",
  transition: "width 200ms ease-linear",
  "[data-state='collapsed'] &": {
    width: 0,
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    width: "var(--sidebar-width-icon)",
  },
  "[data-side='right'] &": {
    transform: "rotate(180deg)",
  },
};

const sidebarContainerStyles: Interpolation<ThemeType> = (theme) => ({
  position: "fixed",
  insetBlock: 0,
  display: "none",
  height: "100vh",
  width: "var(--sidebar-width)",
  transition:
    "left 200ms ease-linear, right 200ms ease-linear, width 200ms ease-linear",
  "@media (min-width: 768px)": {
    display: "flex",
  },
  "[data-side='left'] &": {
    left: 0,
    borderRight: `1px solid ${theme.semanticColors.neutralsVeryLow}`,
  },
  "[data-side='right'] &": {
    right: 0,
    borderLeft: `1px solid ${theme.semanticColors.neutralsVeryLow}`,
  },
  "[data-state='collapsed'][data-collapsible='offcanvas'][data-side='left'] &":
    {
      left: "calc(var(--sidebar-width) * -1)",
    },
  "[data-state='collapsed'][data-collapsible='offcanvas'][data-side='right'] &":
    {
      right: "calc(var(--sidebar-width) * -1)",
    },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    width: "var(--sidebar-width-icon)",
  },
  "&[data-variant='floating'], &[data-variant='inset']": {
    padding: spacing.s,
    "[data-state='collapsed'][data-collapsible='icon'] &": {
      width: `calc(var(--sidebar-width-icon) + ${spacing.m} + 2px)`,
    },
  },
});

const sidebarInnerStyles: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  display: "flex",
  height: "100%",
  width: "100%",
  flexDirection: "column",
  "[data-variant='floating'] &": {
    borderRadius: radius.m,
    border: `1px solid ${theme.semanticColors.neutralsVeryLow}`,
    boxShadow:
      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  },
});

const sidebarInsetStyles: Interpolation<ThemeType> = (theme) => ({
  position: "relative",
  display: "flex",
  width: "100%",
  flex: 1,
  flexDirection: "column",
  backgroundColor: theme.semanticColors.secondary,
  "@media (min-width: 768px)": {
    "&.peer-data-[variant='inset']": {
      margin: spacing.s,
      marginLeft: 0,
      borderRadius: radius.l,
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      "&.peer-data-[state='collapsed']": {
        marginLeft: spacing.s,
      },
    },
  },
});

const sidebarInputStyles: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.secondary,
  height: "32px",
  width: "100%",
  boxShadow: "none",
});

const sidebarHeaderStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  padding: spacing.s,
};

const sidebarFooterStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  padding: spacing.s,
};

const sidebarSeparatorStyles: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLower,
  marginInline: spacing.s,
  width: "auto",
  height: "1px",
});

const sidebarContentStyles: Interpolation = {
  display: "flex",
  minHeight: 0,
  flex: 1,
  flexDirection: "column",
  gap: spacing.s,
  overflowY: "auto",
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    overflow: "hidden",
  },
};

const sidebarGroupStyles: Interpolation = {
  position: "relative",
  display: "flex",
  width: "100%",
  minWidth: 0,
  flexDirection: "column",
  padding: spacing.s,
};

const sidebarGroupLabelStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  display: "flex",
  height: "32px",
  flexShrink: 0,
  alignItems: "center",
  borderRadius: radius.m,
  padding: `0 ${spacing.s}`,
  fontSize: "12px",
  fontWeight: 500,
  outline: "none",
  transition: "margin 200ms ease-linear, opacity 200ms ease-linear",
  "&:focus-visible": {
    boxShadow: `0 0 0 2px ${theme.semanticColors.primary}`,
  },
  "& > svg": {
    flexShrink: 0,
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    marginTop: "-32px",
    opacity: 0,
  },
});

const sidebarGroupActionStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  position: "absolute",
  top: "14px",
  right: "12px",
  display: "flex",
  aspectRatio: "1",
  width: "20px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: radius.m,
  padding: 0,
  outline: "none",
  transition: "transform 150ms",
  cursor: "pointer",
  backgroundColor: "transparent",
  border: "none",
  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
  },
  "&:focus-visible": {
    boxShadow: `0 0 0 2px ${theme.semanticColors.primary}`,
  },
  "& > svg": {
    flexShrink: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: "-8px",
  },
  "@media (min-width: 768px)": {
    "&::after": {
      display: "none",
    },
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    display: "none",
  },
});

const sidebarGroupContentStyles: Interpolation = {
  width: "100%",
  fontSize: "14px",
};

const sidebarMenuStyles: Interpolation = {
  display: "flex",
  width: "100%",
  minWidth: 0,
  flexDirection: "column",
  gap: "4px",
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    gap: "8px",
  },
};

const sidebarMenuItemStyles: Interpolation = {
  position: "relative",
};

const sidebarMenuButtonStyles: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: spacing.s,
  overflow: "hidden",
  borderRadius: radius.m,
  padding: spacing.s,
  textAlign: "left",
  fontSize: "14px",
  outline: "none",
  transition: "width 150ms, height 150ms, padding 150ms",
  cursor: "pointer",
  backgroundColor: "transparent",
  border: "none",
  color: theme.semanticColors.primary,
  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
  },
  "&:focus-visible": {
    boxShadow: `0 0 0 2px ${theme.semanticColors.primary}`,
  },
  "&:active": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
  },
  "&:disabled": {
    pointerEvents: "none",
    opacity: 0.5,
  },
  "[data-sidebar='menu-action'] ~ &": {
    paddingRight: "32px",
  },
  "&[aria-disabled='true']": {
    pointerEvents: "none",
    opacity: 0.5,
  },
  "& > span:last-child": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& > svg": {
    flexShrink: 0,
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    width: "40px !important",
    height: "40px !important",
    padding: "8px !important",
    borderRadius: "50% !important",
  },
});

const sidebarMenuButtonActiveStyles: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  fontWeight: 500,
  color: theme.semanticColors.neutralsHigher,
});

const sidebarMenuButtonOutlineStyles = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.secondary,
  boxShadow: `0 0 0 1px ${theme.semanticColors.neutralsLower}`,
  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
    boxShadow: `0 0 0 1px ${theme.semanticColors.neutralsLowest}`,
  },
});

const sidebarMenuButtonSizeStyles = {
  default: css({
    height: "32px",
    fontSize: "14px",
  }),
  sm: css({
    height: "28px",
    fontSize: "12px",
  }),
  lg: css({
    height: "48px",
    fontSize: "14px",
    "[data-state='collapsed'][data-collapsible='icon'] &": {
      padding: "0 !important",
    },
  }),
};

const sidebarMenuActionStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  position: "absolute",
  top: "6px",
  right: "4px",
  display: "flex",
  aspectRatio: "1",
  width: "20px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: radius.m,
  padding: 0,
  outline: "none",
  transition: "transform 150ms",
  cursor: "pointer",
  backgroundColor: "transparent",
  border: "none",
  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
  },
  "&:focus-visible": {
    boxShadow: `0 0 0 2px ${theme.semanticColors.primary}`,
  },
  "& > svg": {
    flexShrink: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: "-8px",
  },
  "@media (min-width: 768px)": {
    "&::after": {
      display: "none",
    },
  },
  "[data-sidebar='menu-button'][data-size='sm'] ~ &": {
    top: "4px",
  },
  "[data-sidebar='menu-button'][data-size='default'] ~ &": {
    top: "6px",
  },
  "[data-sidebar='menu-button'][data-size='lg'] ~ &": {
    top: "10px",
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    display: "none",
  },
});

const sidebarMenuBadgeStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  pointerEvents: "none",
  position: "absolute",
  right: "4px",
  display: "flex",
  height: "20px",
  minWidth: "20px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: radius.m,
  paddingInline: "4px",
  fontSize: "12px",
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
  userSelect: "none",
  "[data-sidebar='menu-button']:hover ~ &": {
    color: theme.semanticColors.neutralsHigher,
  },
  "[data-sidebar='menu-button'][data-active='true'] ~ &": {
    color: theme.semanticColors.neutralsHigher,
  },
  "[data-sidebar='menu-button'][data-size='sm'] ~ &": {
    top: "4px",
  },
  "[data-sidebar='menu-button'][data-size='default'] ~ &": {
    top: "6px",
  },
  "[data-sidebar='menu-button'][data-size='lg'] ~ &": {
    top: "10px",
  },
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    display: "none",
  },
});

const sidebarMenuSkeletonStyles: Interpolation = {
  display: "flex",
  height: "32px",
  alignItems: "center",
  gap: spacing.s,
  borderRadius: radius.m,
  paddingInline: spacing.s,
};

const sidebarMenuSubStyles: Interpolation<ThemeType> = (theme) => ({
  marginInline: "14px",
  display: "flex",
  minWidth: 0,
  transform: "translateX(1px)",
  flexDirection: "column",
  gap: "4px",
  borderLeft: `1px solid ${theme.semanticColors.neutralsLower}`,
  paddingInline: "10px",
  paddingBlock: "2px",
  "[data-state='collapsed'][data-collapsible='icon'] &": {
    display: "none",
  },
});

const sidebarMenuSubItemStyles: Interpolation = {
  position: "relative",
};

const getSidebarMenuSubButtonStyles = (
  theme: ThemeType,
  size: "sm" | "md",
  isActive: boolean,
): Interpolation => {
  const baseStyles = css({
    color: theme.semanticColors.primary,
    display: "flex",
    height: "28px",
    minWidth: 0,
    transform: "translateX(-1px)",
    alignItems: "center",
    gap: spacing.s,
    overflow: "hidden",
    borderRadius: radius.m,
    paddingInline: spacing.s,
    outline: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    textDecoration: "none",
    "&:hover": {
      backgroundColor: theme.semanticColors.neutralsLowest,
      color: theme.semanticColors.neutralsHigher,
    },
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${theme.semanticColors.primary}`,
    },
    "&:active": {
      backgroundColor: theme.semanticColors.neutralsLowest,
      color: theme.semanticColors.neutralsHigher,
    },
    "&:disabled": {
      pointerEvents: "none",
      opacity: 0.5,
    },
    "&[aria-disabled='true']": {
      pointerEvents: "none",
      opacity: 0.5,
    },
    "& > span:last-child": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& > svg": {
      flexShrink: 0,
      color: theme.semanticColors.neutralsHigher,
    },
    "[data-state='collapsed'][data-collapsible='icon'] &": {
      display: "none",
    },
  });

  const activeStyles = isActive
    ? css({
        backgroundColor: theme.semanticColors.neutralsLowest,
        color: theme.semanticColors.neutralsHigher,
      })
    : null;

  const sizeStyles =
    size === "sm"
      ? css({ fontSize: "0.75rem" })
      : css({ fontSize: "0.875rem" });

  return [baseStyles, activeStyles, sizeStyles];
};

// Components
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isDesktop = useIsDesktopViewport();
  const isMobile = !isDesktop;
  const [openMobile, setOpenMobile] = React.useState(false);

  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          css={sidebarWrapperStyles}
          className={className}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const theme = useThemeValues();
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        css={sidebarStyles}
        className={className}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          css={css({
            backgroundColor: theme.semanticColors.background,
            color: theme.semanticColors.primary,
            width: "var(--sidebar-width)",
            padding: 0,
            "& > button": {
              display: "none",
            },
          })}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader
            css={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              borderWidth: 0,
            }}
          >
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div
            css={{
              display: "flex",
              height: "100%",
              width: "100%",
              flexDirection: "column",
            }}
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      css={{
        display: "none",
        "@media (min-width: 768px)": { display: "block" },
        color: theme.semanticColors.primary,
      }}
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      className={className}
    >
      <div data-slot="sidebar-gap" css={sidebarGapStyles} />
      <div
        data-slot="sidebar-container"
        css={[
          sidebarContainerStyles,
          variant === "floating" || variant === "inset"
            ? css({ padding: spacing.s })
            : null,
        ]}
        data-variant={variant}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          css={sidebarInnerStyles}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: Partial<IconButtonProps>) {
  const { toggleSidebar } = useSidebar();

  return (
    <IconButton
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      className={className}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      icon={<PanelLeftIcon size={18} />}
      size="s"
      variant="transparentPrimary"
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      css={sidebarInsetStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      css={sidebarInputStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      css={sidebarHeaderStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      css={sidebarFooterStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<"hr">) {
  const theme = useThemeValues();

  return (
    <hr
      data-slot="sidebar-separator"
      data-sidebar="separator"
      css={sidebarSeparatorStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      css={sidebarContentStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      css={sidebarGroupStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const theme = useThemeValues();
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      css={sidebarGroupLabelStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      css={sidebarGroupActionStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      css={sidebarGroupContentStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      css={sidebarMenuStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      css={sidebarMenuItemStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  children,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  variant?: "default" | "outline";
  size?: "default" | "sm" | "lg";
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  children?: React.ReactNode;
}) {
  const Comp = asChild ? Slot : "button";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      css={[
        sidebarMenuButtonStyles,
        isActive && sidebarMenuButtonActiveStyles,
        variant === "outline" && sidebarMenuButtonOutlineStyles,
        sidebarMenuButtonSizeStyles[
          size as keyof typeof sidebarMenuButtonSizeStyles
        ],
      ]}
      className={className}
      {...props}
    >
      {children}
    </Comp>
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        css={{
          display: state !== "collapsed" || isMobile ? "none" : undefined,
        }}
        {...tooltip}
      />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const theme = useThemeValues();
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      css={[
        sidebarMenuActionStyles,
        showOnHover &&
          css({
            opacity: 0,
            "@media (min-width: 768px)": {
              "[data-sidebar='menu-button'][data-active='true'] ~ &": {
                color: theme.semanticColors.neutralsHigher,
              },
              ".group:focus-within &, .group:hover &": {
                opacity: 1,
              },
              "&[data-state='open']": {
                opacity: 1,
              },
            },
          }),
      ]}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const theme = useThemeValues();

  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      css={sidebarMenuBadgeStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) {
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      css={sidebarMenuSkeletonStyles}
      className={className}
      {...props}
    >
      {showIcon && (
        <Skeleton
          css={{ width: "16px", height: "16px", borderRadius: radius.m }}
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        css={{ height: "16px", maxWidth: "var(--skeleton-width)", flex: 1 }}
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  const theme = useThemeValues();

  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      css={sidebarMenuSubStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      css={sidebarMenuSubItemStyles}
      className={className}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) {
  const theme = useThemeValues();
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      css={getSidebarMenuSubButtonStyles(theme, size, isActive)}
      className={className}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
